import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import type { UnitWeight } from "../types";
import { displayToKg, kgToDisplay } from "../types";
import { ScreenShell } from "./ScreenShell";

const MIN_WEIGHT_KG = 30;
const MAX_WEIGHT_KG = 300;

function formatDisplay(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

interface WeightStepProps {
  readonly title: string;
  readonly helperText?: string;
  readonly valueKg?: number;
  readonly unit: UnitWeight;
  readonly onUnitChange: (unit: UnitWeight) => void;
  readonly onContinue: (weightKg: number) => void;
  /** Directional guard for the goal-weight step; returns blocking copy or null. */
  readonly warningFor?: (candidateKg: number) => string | null;
}

/** Huge centered weight input with a kg/lbs segmented toggle that converts in place. */
export function WeightStep({
  title,
  helperText,
  valueKg,
  unit,
  onUnitChange,
  onContinue,
  warningFor,
}: WeightStepProps) {
  const [display, setDisplay] = useState(() =>
    valueKg !== undefined ? formatDisplay(kgToDisplay(valueKg, unit)) : "",
  );

  const parsed = display.trim() === "" ? undefined : Number(display);
  const candidateKg =
    parsed !== undefined && Number.isFinite(parsed) ? displayToKg(parsed, unit) : undefined;
  const inRange =
    candidateKg !== undefined && candidateKg >= MIN_WEIGHT_KG && candidateKg <= MAX_WEIGHT_KG;
  const warning = candidateKg !== undefined && warningFor ? warningFor(candidateKg) : null;
  const canContinue = inRange && warning === null;

  const handleUnitChange = (next: UnitWeight) => {
    if (next === unit) return;
    if (candidateKg !== undefined) {
      setDisplay(formatDisplay(kgToDisplay(candidateKg, next)));
    }
    onUnitChange(next);
  };

  return (
    <ScreenShell
      title={title}
      subtext={helperText}
      center
      footer={
        <>
          {warning !== null && inRange && (
            <p role="alert" className="text-center text-sm font-medium text-warning">
              {warning}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!canContinue}
            onClick={() => {
              if (canContinue && candidateKg !== undefined) onContinue(candidateKg);
            }}
          >
            Continue
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-6">
        <div className="flex items-end justify-center gap-2">
          <input
            type="text"
            inputMode="decimal"
            aria-label={title}
            value={display}
            onChange={(e) => setDisplay(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder={unit === "kg" ? "70" : "154"}
            className="w-48 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-5xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <span className="pb-4 text-lg font-medium text-text-muted">{unit}</span>
        </div>
        <div
          role="group"
          aria-label="Weight unit"
          className="flex rounded-xl border border-border bg-surface p-1"
        >
          {(["kg", "lbs"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={unit === option}
              onClick={() => handleUnitChange(option)}
              className={cn(
                "min-h-[44px] min-w-[72px] rounded-lg px-4 text-base font-medium",
                "select-none transition-all duration-200 ease-out active:scale-[0.98]",
                unit === option ? "bg-accent text-white" : "text-text-muted",
              )}
            >
              {option}
            </button>
          ))}
        </div>
        {parsed !== undefined && !inRange && (
          <p role="alert" className="text-sm text-danger">
            Enter a weight between {kgToDisplay(MIN_WEIGHT_KG, unit)} and{" "}
            {kgToDisplay(MAX_WEIGHT_KG, unit)} {unit}.
          </p>
        )}
      </div>
    </ScreenShell>
  );
}

interface SimpleNumberStepProps {
  readonly title: string;
  readonly subtext?: string;
  readonly unitLabel: string;
  readonly placeholder: string;
  readonly min: number;
  readonly max: number;
  readonly value?: number;
  readonly onContinue: (value: number) => void;
}

/** Huge centered single-unit number input (height, age) with explicit Continue. */
export function SimpleNumberStep({
  title,
  subtext,
  unitLabel,
  placeholder,
  min,
  max,
  value,
  onContinue,
}: SimpleNumberStepProps) {
  const [display, setDisplay] = useState(() => (value !== undefined ? String(value) : ""));
  const parsed = display.trim() === "" ? undefined : Number(display);
  const valid = parsed !== undefined && Number.isFinite(parsed) && parsed >= min && parsed <= max;

  return (
    <ScreenShell
      title={title}
      subtext={subtext}
      center
      footer={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!valid}
          onClick={() => {
            if (valid && parsed !== undefined) onContinue(parsed);
          }}
        >
          Continue
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-6">
        <div className="flex items-end justify-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            aria-label={title}
            value={display}
            onChange={(e) => setDisplay(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder={placeholder}
            className="w-48 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-5xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <span className="pb-4 text-lg font-medium text-text-muted">{unitLabel}</span>
        </div>
        {parsed !== undefined && !valid && (
          <p role="alert" className="text-sm text-danger">
            Enter a value between {min} and {max}.
          </p>
        )}
      </div>
    </ScreenShell>
  );
}
